import { useState, useEffect, useRef } from 'react';
import { Translate, translate } from 'react-i18nify';
import { useHistory, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { APPLICATION_BASE_PATH } from '../../../constants';
import Card from '../../Card';
import Select from '../../Select';
import { usePartner, PartnerServiceState, Facility, BookingType } from '../PartnerProvider';
import { PartnerFacilityParams } from '..';
import LoaderButton from '../../LoaderButton';
import DropdownIndicator from '../../DropdownIndicator';
import { cleanUpFilename, toggleItem } from '../../../utils';
import RadioButton from '../../RadioButton';
import { Divider } from '@material-ui/core';

export default function CreatePartnerFacility({ editMode }) {
  const history = useHistory();
  const {
    createPartnerFacility,
    getPartnerFacility,
    updatePartnerFacility,
    addVisitationInfoToFacility,
    updateVisitationInfoForFacility,
    removePartnerFacility,
    getRegions,
    getLocationFromAddress,
    toast,
    partners,
    facilityServices
  } = usePartner();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const { partnerId, facilityId } = useParams<PartnerFacilityParams>();
  const [facility, setFacility] = useState<Facility>();
  const [active, setActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchingFacility, setFetchingFacility] = useState(false);
  const [instructionsFilename, setInstructionsFilename] = useState<string>();
  const [instructionsFile, setInstructionsFile] = useState<File | undefined>();
  const [removingFacility, setRemovingFacility] = useState(false);
  const servicesRequiringVisitationInfo = ['auscultation', 'examination', 'labSampling'];
  const requiresVisitationInfo = selectedServices.some((s) => servicesRequiringVisitationInfo.includes(s));
  const newFacilityId = useRef<string>();
  const [streetAddressValid, setStreetAddressValid] = useState(true);
  const [selectedBookingType, setSelectedBookingType] = useState<BookingType | undefined>();
  const [regions, setRegions] = useState<Region[]>([]);
  const [fetchingRegions, setFetchingRegions] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>();
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [fetchingLocationFromAddress, setFetchingLocationFromAddress] = useState(false);
  const [couldNotFindLocationError, setCouldNotFindLocationError] = useState<string | undefined>();
  const [detailsForBooking, setDetailsForBooking] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);

  const servicesOptions = (
    partners
      .find((p) => p.id === partnerId)
      ?.services?.filter((s) => s.selected && facilityServices.includes(s.service))
      .map((s) => s.service) ?? facilityServices
  ).map((service) => {
    return { value: service, label: translate(`manage_partners.service.${service}`) };
  });

  useEffect(() => {
    getRegions().then((res) => {
      setFetchingRegions(false);
      if (res.length > 0) {
        setRegions(res);
      }
    });
    if (editMode) {
      setFetchingFacility(true);
      getPartnerFacility(partnerId, facilityId)
        .then((res) => {
          if (res.facilities?.length) {
            const facility = res.facilities[0];
            setFacility(facility);
            setLatitude(facility.latitude?.toString() ?? '');
            setLongitude(facility.longitude?.toString() ?? '');
            setSelectedServices(
              facility?.services
                ?.filter((s) => s.selected && facilityServices.includes(s.service))
                .map((s) => s.service) ?? []
            );
            setActive(!!facility?.active);
            setInstructionsFilename(
              facility?.visitationInfo?.instructions?.filename
                ? cleanUpFilename(facility?.visitationInfo?.instructions?.filename)
                : undefined
            );
            setSelectedBookingType(facility?.visitationInfo?.bookingType);
            if (facility?.visitationInfo?.bookingType === 'DROP_IN') {
              setDetailsForBooking(facility?.visitationInfo?.businessHoursLink ?? '');
            }
            if (facility?.visitationInfo?.bookingType === 'PHONE_BOOKING') {
              setDetailsForBooking(facility?.visitationInfo?.bookingPhoneNumber ?? '');
            }
            if (facility?.visitationInfo?.bookingType === 'WEB_BOOKING') {
              setDetailsForBooking(facility?.visitationInfo?.bookingLink ?? '');
            }
          }
        })
        .finally(() => setFetchingFacility(false));
    }
  }, []);

  useEffect(() => {
    if (regions.length && facility?.regionId) {
      setSelectedRegion(regions.find((region) => region.id == facility.regionId));
    }
  }, [regions, facility]);

  const getFacilityFromFormData = (e) => {
    const formData = new FormData(e.target);

    const services = facilityServices.map(
      (s) => ({ service: s, selected: selectedServices.includes(s), disabled: false }) as PartnerServiceState
    );

    services.push({ service: 'admin', selected: true, disabled: false });

    const latRaw = formData.get('latitude');
    const longRaw = formData.get('longitude');
    const facility = {
      name: formData.get('name')?.toString(),
      streetaddress: formData.get('streetaddress')?.toString(),
      city: formData.get('city')?.toString(),
      postalCode: formData.get('postalCode')?.toString().replace(' ', ''),
      phoneNumber: formData.get('phoneNumber')?.toString(),
      email: formData.get('email')?.toString(),
      partnerRegion: formData.get('partnerRegion')?.toString(),
      partnerCostCenter: formData.get('partnerCostCenter')?.toString(),
      services,
      active,
      regionId: selectedServices.includes('labSampling') && selectedRegion ? selectedRegion.id : undefined,
      latitude: latRaw ? parseFloat(latRaw as string) : undefined,
      longitude: longRaw ? parseFloat(longRaw as string) : undefined
    };

    return facility;
  };

  const getVisitationInfoFromFormData = (e) => {
    const formData = new FormData(e.target);
    const cancellationPolicy = formData.get('cancellationPolicy')?.toString();

    const visitationInfo = {
      distance: formData.get('distance')?.toString(),
      cancellationPolicy: cancellationPolicy || undefined,
      filename: instructionsFile ? instructionsFilename : undefined,
      instructionsFile: instructionsFile ? instructionsFile : undefined,
      priority: Number(formData.get('priority')),
      bookingTypes: selectedBookingType,
      bookingPhoneNumber: selectedBookingType === 'PHONE_BOOKING' ? detailsForBooking : undefined,
      bookingLink: selectedBookingType === 'WEB_BOOKING' ? detailsForBooking : undefined,
      businessHoursLink: selectedBookingType === 'DROP_IN' ? detailsForBooking : undefined
    };

    return visitationInfo;
  };

  const updateFacility = async (e) => {
    e.preventDefault();

    if (!streetAddressValid) {
      return;
    }

    const updatedFacility = getFacilityFromFormData(e);
    const visitationInfo = getVisitationInfoFromFormData(e);

    try {
      setIsLoading(true);
      const facility = await updatePartnerFacility(partnerId, facilityId, updatedFacility);
      history.push(`/${APPLICATION_BASE_PATH}/services/partners/${partnerId}/facility/${facilityId}`);
      if (
        requiresVisitationInfo &&
        (visitationInfo.cancellationPolicy !== facility.visitationInfo?.cancellationPolicy ||
          visitationInfo.distance !== facility.visitationInfo?.distance?.toString() ||
          visitationInfo.filename !== facility.visitationInfo?.instructions?.filename)
      ) {
        await updateVisitationInfoForFacility(
          partnerId,
          facility.id!,
          visitationInfo.distance,
          visitationInfo.filename,
          visitationInfo.instructionsFile,
          visitationInfo.cancellationPolicy,
          visitationInfo.businessHoursLink,
          visitationInfo.bookingLink,
          visitationInfo.priority,
          visitationInfo.bookingTypes,
          visitationInfo.bookingPhoneNumber
        );
      }
    } catch (err) {
      console.log(err);
      toast(translate('notification.update_facility.error'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const createNewFacility = async (e) => {
    e.preventDefault();

    const newFacility = getFacilityFromFormData(e);

    try {
      setIsLoading(true);
      if (!newFacilityId.current) {
        const facility = await createPartnerFacility(partnerId, newFacility);

        newFacilityId.current = facility.id!;
      }
      if (requiresVisitationInfo) {
        const visitationInfo = getVisitationInfoFromFormData(e);

        if (!newFacilityId.current) {
          throw new Error('Facility ID missing');
        }

        if (!visitationInfo.distance) {
          throw new Error('VisitationInfo field(s) missing');
        }

        await addVisitationInfoToFacility(
          partnerId,
          newFacilityId.current,
          visitationInfo.distance,
          visitationInfo.filename,
          visitationInfo.instructionsFile,
          visitationInfo.cancellationPolicy,
          visitationInfo.businessHoursLink,
          visitationInfo.bookingLink,
          visitationInfo.priority,
          visitationInfo.bookingTypes,
          visitationInfo.bookingPhoneNumber
        );
      }
      history.push(`/${APPLICATION_BASE_PATH}/services/partners/${partnerId}/facility/${newFacilityId.current}`);
    } catch (err) {
      console.log(err);
      toast(translate('notification.create_facility.error'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedServicesChange = (_, event) => {
    switch (event.action) {
      case 'clear':
        setSelectedServices([]);
        break;
      case 'remove-value':
        setSelectedServices(toggleItem(selectedServices, event.removedValue.value));
        break;
      case 'select-option':
        setSelectedServices(toggleItem(selectedServices, event.option.value));
        break;
      default:
        return;
    }
  };

  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setInstructionsFilename(file.name);
    setInstructionsFile(file);
  };

  const remove = async () => {
    if (window.confirm(translate('manage_partners.facilities.confirm_delete'))) {
      setRemovingFacility(true);
      try {
        await removePartnerFacility(partnerId, facilityId);
        history.replace(`/${APPLICATION_BASE_PATH}/services/partners/${partnerId}`);
      } catch (err) {
        console.log(err);
        toast(translate('notification.remove_facility.error'), 'error');
      } finally {
        setRemovingFacility(false);
      }
    }
  };

  const variants = {
    open: {
      height: '100%'
    },
    closed: {
      height: 0
    }
  };

  const bookingTypes: BookingType[] = ['DROP_IN', 'PHONE_BOOKING', 'WEB_BOOKING'];

  const dynamicFieldNameFromBookingType = (bookingType: BookingType) => {
    if (bookingType === 'DROP_IN') {
      return 'businessHoursLink';
    }
    if (bookingType === 'PHONE_BOOKING') {
      return 'bookingPhoneNumber';
    }
    if (bookingType === 'WEB_BOOKING') {
      return 'bookingLink';
    }
    return '';
  };

  const fetchLatLongFromAddress = async () => {
    if (!formRef.current) return;

    setFetchingLocationFromAddress(true);
    setCouldNotFindLocationError(undefined);
    const formData = new FormData(formRef.current);
    const streetAddress = formData.get('streetaddress')?.toString() ?? '';
    const city = formData.get('city')?.toString() ?? '';
    const postalCode = formData.get('postalCode')?.toString().replace(' ', '') ?? '';

    try {
      const response = await getLocationFromAddress(streetAddress, postalCode, city);
      setFetchingLocationFromAddress(false);
      if (!response.ok) {
        throw new Error();
      }

      const data = await response.json();
      setLatitude(data?.lat?.toString());
      setLongitude(data?.lng?.toString());
    } catch (e) {
      setCouldNotFindLocationError(translate('manage_partners.facilities.could_not_find_location_error'));
      setFetchingLocationFromAddress(false);
    }
  };

  return (
    <Card
      titleI18nKey={
        editMode
          ? 'manage_partners.facilities.edit_header'
          : partners.length
            ? translate(`manage_partners.facilities.create_new_header`, {
                partner: partners.find((p) => p.id === partnerId)?.name ?? ''
              })
            : ''
      }
      loadingTitle={fetchingFacility}
      errorI18nKey="manage_partners.error"
      additionalClass="x-large"
      isLoading={fetchingFacility}
    >
      <form ref={formRef} onSubmit={editMode ? updateFacility : createNewFacility} className="partner-form">
        <div className="mb-10">
          <h4>
            <Translate value="manage_partners.partner_services.select_services" />
          </h4>
          <Select
            isMulti
            disabled={fetchingFacility}
            closeMenuOnSelect
            placeholder={translate('manage_partners.partner_services.available_services')}
            noOptionsMessage={() => <Translate value="manage_partners.partner_services.all_selected" />}
            isSearchable={false}
            onChange={selectedServicesChange}
            classNamePrefix="multi-select"
            components={{ DropdownIndicator }}
            options={servicesOptions}
            value={selectedServices.map((s) => ({
              value: s,
              label: translate(`manage_partners.service.${s}`)
            }))}
          />
        </div>

        <div className="mb-20">
          <h4>
            <Translate value="manage_partners.facilities.status" />
          </h4>
          <input
            type="checkbox"
            checked={active}
            id="facility-active-checkbox"
            onChange={() => setActive(!active)}
            disabled={fetchingFacility}
          />
          <label htmlFor="facility-active-checkbox" className="ml-5 fw-500">
            <Translate value="manage_partners.status.active" />
          </label>
        </div>

        <h3>
          <Translate value="manage_partners.facilities.facility_info_header" />
        </h3>

        <div className="fs-14 fw-normal mb-15">
          <Translate value="manage_partners.facilities.facility_info_body" />
        </div>

        <label>
          <h4>
            <Translate value="manage_partners.facilities.name" />
          </h4>
          <input
            type="text"
            name="name"
            autoComplete="off"
            defaultValue={facility?.name}
            required
            disabled={fetchingFacility}
            placeholder={translate('manage_partners.facilities.name_placeholder')}
          />
        </label>
        <label>
          <h4>
            <Translate value="member_details.street_address" />
            {!streetAddressValid ? (
              <span className="error-message ml-10">Kommatecken tillåts inte i gatuadressen</span>
            ) : null}
          </h4>
          <input
            type="text"
            name="streetaddress"
            autoComplete="off"
            defaultValue={facility?.streetaddress}
            required
            disabled={fetchingFacility}
            onBlur={(e) => setStreetAddressValid(!e.target.value.includes(','))}
            placeholder={translate('manage_partners.facilities.address_placeholder')}
          />
        </label>
        <div className="flex">
          <label className="mr-10">
            <h4>
              <Translate value="member_details.postal_code" />
            </h4>
            <input
              type="text"
              name="postalCode"
              autoComplete="off"
              defaultValue={facility?.postalCode}
              required
              disabled={fetchingFacility}
              pattern="[0-9\s]{5,6}"
              placeholder={translate('manage_partners.facilities.postal_code_placeholder')}
            />
          </label>
          <label>
            <h4>
              <Translate value="member_details.city" />
            </h4>
            <input
              type="text"
              name="city"
              autoComplete="off"
              defaultValue={facility?.city}
              required
              disabled={fetchingFacility}
              pattern="[^,]+"
              placeholder={translate('manage_partners.facilities.city_placeholder')}
            />
          </label>
        </div>

        <motion.div
          className="overflow-hidden"
          animate={requiresVisitationInfo ? 'open' : 'closed'}
          initial="closed"
          variants={variants}
        >
          {requiresVisitationInfo ? (
            <>
              <div className="flex">
                <label className="mr-10">
                  <h4>
                    <Translate value="manage_partners.facilities.latitude" />
                  </h4>
                  <input
                    type="text"
                    name="latitude"
                    value={latitude ?? ''}
                    onChange={(e) => setLatitude(e.target.value)}
                    autoComplete="off"
                    required
                    disabled={fetchingFacility}
                    pattern="^-?\d+(\.\d+)?$"
                    placeholder={translate('manage_partners.facilities.latitude_placeholder')}
                  />
                </label>
                <label>
                  <h4>
                    <Translate value="manage_partners.facilities.longitude" />
                  </h4>
                  <input
                    type="text"
                    name="longitude"
                    value={longitude ?? ''}
                    onChange={(e) => setLongitude(e.target.value)}
                    autoComplete="off"
                    required
                    disabled={fetchingFacility}
                    pattern="^-?\d+(\.\d+)?$"
                    placeholder={translate('manage_partners.facilities.longitude_placeholder')}
                  />
                </label>
              </div>

              {!!couldNotFindLocationError ? <h4 className="negative mt-10">{couldNotFindLocationError}</h4> : null}

              <LoaderButton
                className="mt-20 mb-10"
                isLoading={fetchingLocationFromAddress}
                onClick={fetchLatLongFromAddress}
              >
                <Translate value="manage_partners.facilities.fetch_location_button_title" />
              </LoaderButton>
            </>
          ) : null}
        </motion.div>

        <motion.div
          className="overflow-hidden"
          animate={selectedServices.includes('labSampling') ? 'open' : 'closed'}
          initial="closed"
          variants={variants}
        >
          <label>
            <h4>
              <Translate value="manage_partners.facilities.way_of_booking" />
            </h4>

            {bookingTypes.map((bookingType) => (
              <div key={bookingType} className="mb-5">
                <RadioButton
                  label={translate(`manage_partners.facilities.booking_type.${bookingType}`)}
                  name="bookingType"
                  value={bookingType}
                  onChange={(_) => {
                    setDetailsForBooking('');
                    setSelectedBookingType(bookingType);
                  }}
                  checked={bookingType === selectedBookingType}
                  className={bookingType === selectedBookingType ? 'fw-bold' : undefined}
                />
              </div>
            ))}
          </label>

          {selectedBookingType ? (
            <>
              <label>
                <h4>
                  <Translate value="manage_partners.facilities.details_for_booking" />
                </h4>
                <input
                  type="text"
                  name={dynamicFieldNameFromBookingType(selectedBookingType)}
                  value={detailsForBooking ?? ''}
                  onChange={(e) => setDetailsForBooking(e.target.value)}
                  autoComplete="off"
                  defaultValue={facility?.visitationInfo?.[dynamicFieldNameFromBookingType(selectedBookingType)]}
                  required
                  disabled={fetchingFacility}
                  placeholder={translate(
                    `manage_partners.facilities.details_for_booking_placeholder.${selectedBookingType}`
                  )}
                />
              </label>
            </>
          ) : null}
        </motion.div>

        <Divider className="mb-20 mt-20" />

        <h3 className="mb-20">
          <Translate value="manage_partners.facilities.internal_info_header" />
        </h3>

        <motion.div
          className="overflow-hidden"
          animate={requiresVisitationInfo ? 'open' : 'closed'}
          initial="closed"
          variants={variants}
        >
          <label>
            <h4>
              <Translate value="manage_partners.facilities.acceptable_distance" />
            </h4>
            <input
              type="text"
              name="distance"
              autoComplete="off"
              defaultValue={facility?.visitationInfo?.distance}
              required={requiresVisitationInfo}
              disabled={fetchingFacility}
              placeholder={translate('manage_partners.facilities.distance_placeholder')}
            />
          </label>

          <label>
            <h4>
              <Translate value="manage_partners.facilities.prio" />
            </h4>
            <input
              type="number"
              name="priority"
              autoComplete="off"
              defaultValue={facility?.visitationInfo?.priority}
              disabled={fetchingFacility}
              placeholder={translate('manage_partners.facilities.priority_placeholder')}
            />
          </label>

          <div className="mt-20">
            <h4>
              <Translate value="manage_partners.facilities.instructions_file" />
            </h4>
            <input
              type="file"
              name="instructions"
              id="facility-instructions-file-input"
              accept=".pdf"
              onChange={onFileInputChange}
              required={false}
              disabled={fetchingFacility}
            />
            <label htmlFor="facility-instructions-file-input">
              <Translate value="manage_partners.facilities.select_file" />
            </label>
          </div>

          <label>
            <h4>
              <Translate value="manage_partners.facilities.cancellation_policy" />
            </h4>
            <textarea
              name="cancellationPolicy"
              defaultValue={facility?.visitationInfo?.cancellationPolicy}
              disabled={fetchingFacility}
              placeholder={translate('manage_partners.facilities.text_placeholder')}
            />
          </label>
        </motion.div>

        {selectedServices.includes('labSampling') && selectedServices.length == 1 ? null : (
          <label>
            <h4>
              <Translate value="manage_partners.facilities.region" />
            </h4>
            <input
              type="text"
              name="partnerRegion"
              autoComplete="off"
              defaultValue={facility?.partnerRegion}
              disabled={fetchingFacility}
              placeholder={translate('manage_partners.facilities.text_placeholder')}
            />
          </label>
        )}
        <label>
          <h4>
            <Translate value="manage_partners.facilities.cost_center" />
          </h4>
          <input
            type="text"
            name="partnerCostCenter"
            autoComplete="off"
            defaultValue={facility?.partnerCostCenter}
            disabled={fetchingFacility}
            placeholder={translate('manage_partners.facilities.text_placeholder')}
          />
        </label>

        <motion.div
          animate={selectedServices.includes('labSampling') ? 'open' : 'closed'}
          initial="closed"
          variants={variants}
        >
          {selectedServices.includes('labSampling') ? (
            <div className="mb-20 mt-20">
              <h4>
                <Translate value="manage_partners.facilities.region" />
              </h4>
              <Select
                disabled={fetchingRegions}
                noOptionsMessage={translate('manage_partners.facilities.region_placeholder')}
                placeholder={translate('manage_partners.facilities.region_placeholder')}
                isSearchable={false}
                onChange={(region) => {
                  setSelectedRegion(region.value);
                }}
                classNamePrefix="multi-select"
                components={{ DropdownIndicator }}
                options={regions.map((r) => {
                  return { value: r, label: r.name };
                })}
                value={
                  selectedRegion
                    ? {
                        value: selectedRegion,
                        label: selectedRegion?.name
                      }
                    : undefined
                }
              />
            </div>
          ) : null}
        </motion.div>

        <Divider className="mb-20 mt-20" />

        <h3 className="mb-20">
          <Translate value="manage_partners.facilities.internal_contact_info_header" />
        </h3>

        <label>
          <h4>
            <Translate value="manage_partners.facilities.phone" />
          </h4>
          <input
            type="text"
            name="phoneNumber"
            autoComplete="off"
            defaultValue={facility?.phoneNumber}
            disabled={fetchingFacility}
            placeholder={translate('manage_partners.facilities.text_placeholder')}
          />
        </label>
        <label>
          <h4>
            <Translate value="manage_partners.facilities.email" />
          </h4>
          <input
            type="text"
            name="email"
            autoComplete="off"
            defaultValue={facility?.email}
            disabled={fetchingFacility}
            placeholder={translate('manage_partners.facilities.text_placeholder')}
          />
        </label>

        <div className="card-buttons">
          {editMode ? (
            <LoaderButton isLoading={removingFacility} onClick={remove} buttonType="destructive">
              <Translate value="manage_partners.facilities.button.remove" />
            </LoaderButton>
          ) : null}
          <LoaderButton type="submit" isLoading={isLoading}>
            {editMode ? (
              <Translate value="manage_partners.facilities.button.update" />
            ) : (
              <Translate value="manage_partners.facilities.button.add" />
            )}
          </LoaderButton>
        </div>
      </form>
    </Card>
  );
}
